/* import __COLOCATED_TEMPLATE__ from './components.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { next } from '@ember/runloop';
import { userEntryToNumber } from '@ebiz/pretty-num';
import FilterConstants from '../../filter-constants';
import ErrorTracker from '../../utils/error-tracker';
import type SiteService from '@ebiz/ember-aditools-site/services/aditools-site';
import type OpAmpsService from 'filter-wizard/services/opamps';
import type ComponentsInput from '../../models/inputs/component-selection';
import type ComponentsAdvisor from '../../models/advisors/component-selection';
import type IndividualStageInput from '../../models/inputs/component-selection/individual-stage';
import type SliderInput from '../../models/inputs/component-selection/slider';
import { assert } from '@ember/debug';
import { isNone } from '@ember/utils';
import { tracked as makeTracked } from 'tracked-built-ins';
import { lockInputsErrorsList } from '../../models/advisors/component-selection';

const optimizationList = [
  { text: 'settings.components.optimizations.voltage-range', value: FilterConstants.optimizationType.voltageRange },
  { text: 'settings.components.optimizations.low-noise', value: FilterConstants.optimizationType.noise },
  { text: 'settings.components.optimizations.low-power', value: FilterConstants.optimizationType.power },
];

interface ComponentSettingsArgs {
  model?: ComponentsInput;
  advisor?: ComponentsAdvisor;
  updateModel?: (inputType: string, modeldata: ComponentsInput) => void;
  updateErrors?: (inputType: string, errors: Array<ValidationError>) => void;
}

type Stage = {
  letter: string;
  settings: IndividualStageInput;
  orderIndex: number;
};

export default class ComponentSettings extends Component<ComponentSettingsArgs> {
  @service('aditools-site') declare siteconfig: SiteService;
  @service declare opamps: OpAmpsService;
  @tracked workingModel: ComponentsInput;
  @tracked errorTracker: ErrorTracker;
  @tracked selectedOptimization: string | undefined;
  @tracked voltageSupplyHelpEnaled: boolean;
  @tracked componentsHelpEnabled: boolean;
  @tracked gbwHelpEnabled: boolean;
  @tracked implementationHelpEnabled: boolean;
  @tracked stages: Stage[];
  @tracked selectedStage: Stage | undefined;
  fieldNames: { [key: string]: string };
  fieldTypes: { [key: string]: string };
  fieldFormat: string;
  optimizationList: { text: string; value: string }[];

  constructor(owner: unknown, args: ComponentSettingsArgs) {
    super(owner, args);
    assert('`Components Input Model` is required', !isNone(this.args.model));
    assert('`Components Advisor` is required', !isNone(this.args.advisor));
    this.workingModel = makeTracked(this.args.advisor.clone(this.args.model) as ComponentsInput);
    this.selectedOptimization =
      this.workingModel.optimizationPreference === FilterConstants.optimizationPreferenceOptions.nonSpecific
        ? this.workingModel.optimization
        : FilterConstants.optimizationType.power;
    this.voltageSupplyHelpEnaled = false;
    this.componentsHelpEnabled = false;
    this.gbwHelpEnabled = false;
    this.implementationHelpEnabled = false;
    this.optimizationList = optimizationList;
    this.fieldNames = FilterConstants.fields.inputs.components;
    this.fieldTypes = {
      vsMinus: FilterConstants.fieldTypes.NumericDecimal,
      vsPlus: FilterConstants.fieldTypes.NumericDecimal,
    };
    this.fieldFormat = '3s';
    this.errorTracker = new ErrorTracker(FilterConstants.tabs.components, this.args.advisor);
    this.stages = [];
    this.sortStages();
    this.selectedStage = this.stages[0];
    // this.addClosestPolyfill(); // checks if .closest() method is present, adds polyfill if not
    next(this, () => {
      const errors = this.errorTracker.validate(this.workingModel);
      if (errors.length > 0 && this.args.updateErrors) {
        this.args.updateErrors('components', errors);
      }
    });
  }

  get isVsPlusValid(): boolean {
    return !this.errorTracker.isInErrorList('vsPlus');
  }

  get isVsMinusValid(): boolean {
    return !this.errorTracker.isInErrorList('vsMinus');
  }

  get opAmpsDisabled(): boolean {
    return (this.args.advisor as ComponentsAdvisor).isComponentResultError(
      this.workingModel,
      this.errorTracker.errorList,
    );
  }

  get isDisabledByErrors(): boolean {
    return this.errorTracker.errorList.some((error: { id: string }) => {
      return lockInputsErrorsList.includes(error.id);
    });
  }

  get displayStages(): boolean {
    return this.workingModel.optimizationPreference === FilterConstants.optimizationPreferenceOptions.specific
      ? true
      : false;
  }

  get hideStages(): boolean {
    return !this.displayStages;
  }

  get optimizationSelection(): { text: string; value: string } | undefined {
    const selected = this.workingModel.optimization;
    const selection = this.optimizationList.find((item) => {
      return item.value === selected;
    });
    return selection;
  }

  // Function that does the following:
  // - Calls Error Tracker to Validate the inputs in the Component
  // - Updates the Error Section if there are errors found
  // - Updates the Model if there are no errors found.
  validateAndUpdateModel(): void {
    const errors = this.errorTracker.validate(this.workingModel);
    if (errors.length > 0) {
      if (this.args.updateErrors) {
        this.args.updateErrors('components', errors);
      }
    } else {
      if (this.args.updateModel && this.args.advisor) {
        this.args.updateModel('components', this.args.advisor.clone(this.workingModel) as ComponentsInput);
      }
    }
  }

  // Function that sets the field input text to the workingModel
  updateModelInput(param: { status: boolean; fieldId: keyof ComponentsInput; fieldValue: string }): void {
    if (param.status) {
      const fieldValue =
        this.fieldTypes[param.fieldId as keyof ComponentsInput] === FilterConstants.fieldTypes.Numeric ||
        this.fieldTypes[param.fieldId as keyof ComponentsInput] === FilterConstants.fieldTypes.NumericDecimal ||
        this.fieldTypes[param.fieldId as keyof ComponentsInput] === FilterConstants.fieldTypes.Engineering
          ? userEntryToNumber(param.fieldValue)
          : param.fieldValue;
      this.workingModel[param.fieldId] = fieldValue;
    }
  }

  // Function that updates the stages with the appropriate OpAmp based on the Optimization Type selected
  updateOpAmpsFromOptimization(): void {
    const workingModel = this.workingModel;
    const optimizationValues = (this.args.advisor as ComponentsAdvisor).getOpAmpsFromOptimization(workingModel);
    const stages = workingModel.individualStageInputs;
    for (let i = 0; i < stages.length; i++) {
      const stageDefaults = (this.args.advisor as ComponentsAdvisor).getStageDefault(i);
      stages[i].opAmp = optimizationValues.opAmps[i];
      stages[i].implementation = stageDefaults ? stageDefaults.implementation : null;
      stages[i].implementationSelection = stageDefaults ? stageDefaults.implementationSelection : null;
      stages[i].componentSizing = stageDefaults ? stageDefaults.componentSizing : null;
      stages[i].gainRatio = stageDefaults ? stageDefaults.gainRatio : null;
    }
    this.workingModel.sortOrder = optimizationValues.sortOrder;
    this.workingModel.individualStageInputs = stages;
    this.sortStages();
  }

  // Function that sorts the individual stage inputs according to sort order
  sortStages(): void {
    const order = this.workingModel.sortOrder;
    const stages = this.workingModel.individualStageInputs;
    const result = [];

    //apply sort order and stage letters
    for (let i = 0; i < order.length; i++) {
      for (let j = 0; j < stages.length; j++) {
        if (order[i] === j) {
          result.push({ letter: stages[order[i]].stageLetter, settings: stages[order[i]], orderIndex: order[i] });
        }
      }
    }
    this.stages = result;
  }

  getSpecificOptimization(): string {
    let isRecommended = true;
    const stages = this.workingModel.individualStageInputs;
    for (let i = 0; i < stages.length; i++) {
      if (
        !(this.args.advisor as ComponentsAdvisor).isRecommendedOpAmp(i, stages[i].opAmp, stages[i], this.workingModel)
      ) {
        isRecommended = false;
        break;
      }
    }
    return isRecommended
      ? FilterConstants.optimizationType.recommendedSpecific
      : FilterConstants.optimizationType.allowedSpecific;
  }

  updateSliders(sliderGainValues: { sliderValues: SliderInput[]; gainValues: SliderInput[] }): void {
    const workingModel = this.workingModel;
    for (let i = 0; i < workingModel.individualStageInputs.length; i++) {
      const individualStageInput = workingModel.individualStageInputs[i];
      individualStageInput.componentSizing = sliderGainValues.sliderValues[i];
      individualStageInput.gainRatio = sliderGainValues.gainValues[i];
    }
  }

  /*
  // fix for ember-sortable, it uses .closest() which is not supported by IE and other browsers below certain versions
  addClosestPolyfill(): void {
    // checks if .closest() method is present, adds polyfill if not
    if (!Element.prototype.matches) {
      Element.prototype.matches = Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
    }
    if (!Element.prototype.closest) {
      Element.prototype.closest = function (el: string) {
        let element = this;
        do {
          if (element.matches(el)) {
            return element;
          }
          element = element.parentElement || element.parentNode;
        } while (element !== null && element.nodeType === 1);
        return null;
      };
    }
  }
  */

  @action fieldChange(param: { status: boolean; fieldId: keyof ComponentsInput; fieldValue: string }): void {
    this.errorTracker.updateUnparseableList(param);
    this.updateModelInput(param);
    if (
      !this.errorTracker.hasUnparsableFields() &&
      this.workingModel.optimizationPreference === FilterConstants.optimizationPreferenceOptions.nonSpecific
    ) {
      this.updateOpAmpsFromOptimization();
    }
    this.validateAndUpdateModel();
  }

  @action updateStage(): void {
    this.workingModel.optimization = this.getSpecificOptimization();
    this.validateAndUpdateModel();
  }

  @action setOptimizationPreference(e: { target: { id: string } }): void {
    const optPref =
      e.target.id === 'specific-radio'
        ? FilterConstants.optimizationPreferenceOptions.specific
        : FilterConstants.optimizationPreferenceOptions.nonSpecific;
    this.workingModel.optimizationPreference = optPref;
    if (this.workingModel.optimizationPreference === FilterConstants.optimizationPreferenceOptions.nonSpecific) {
      this.workingModel.optimization = this.selectedOptimization;
      this.updateOpAmpsFromOptimization();
    } else {
      const workingModel = this.workingModel;
      const optimizedInput = (this.args.advisor as ComponentsAdvisor).getOptimizationValues(workingModel);
      this.updateSliders(optimizedInput);
      this.workingModel.sortOrder = optimizedInput.sortOrder;
      this.workingModel.optimization = this.getSpecificOptimization();
      this.workingModel = workingModel;
      this.sortStages();
    }
    this.validateAndUpdateModel();
  }

  @action setOptimization(e: { value: string }): void {
    this.workingModel.optimization = e.value;
    this.selectedOptimization = this.workingModel.optimization;
    this.updateOpAmpsFromOptimization();
    this.validateAndUpdateModel();
  }

  @action setGBWCompensate(e: { target: { checked: boolean } }): void {
    const compensateForGbw = e.target.checked;
    this.workingModel.compensateForGbw = compensateForGbw;

    this.validateAndUpdateModel();
  }

  @action selectStage(
    stage: { letter: string; settings: IndividualStageInput; orderIndex: number },
    event: { target: { parentElement: { classList: { contains: (arg0: string) => string } } } },
  ): void {
    if (!event.target.parentElement.classList.contains('is-dropping')) {
      this.selectedStage = stage;
    }
  }

  @action updateSortOrder(
    sortedStages: { letter: string; settings: IndividualStageInput; orderIndex: number }[] /*, draggedStage*/,
  ): void {
    this.stages = sortedStages;
    const order = sortedStages.map((value: { orderIndex: number }) => {
      return value.orderIndex;
    });
    this.workingModel.sortOrder = order;
    this.validateAndUpdateModel();
  }
}
